<template>
  <div ref="deskRoot" class="desk-root">
    <div class="desk-topbar">
      <h1 class="page-title">ChatDesk 工作台</h1>
      <div class="topbar-actions">
        <span v-if="!wsUrlConfigured" class="ws-banner ws-banner-warn">即時推播未設定（仍可手動重新整理）</span>
        <span v-else-if="wsSessionConflict" class="ws-banner ws-banner-error">
          此帳號已在其他瀏覽器使用 ChatDesk，正在等待釋放…
        </span>
        <span v-else-if="!wsConnected" class="ws-banner">即時連線中斷，正在重連…（斷線期間可手動重新整理）</span>
        <span v-else class="ws-ok">即時連線中</span>
        <button class="btn btn-secondary btn-sm" type="button" @click="toggleFullscreen">
          <i :class="isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'"></i>
          {{ isFullscreen ? '離開全螢幕' : '全螢幕' }}
        </button>
        <button class="btn btn-secondary btn-sm" type="button" @click="openStandaloneWindow">
          <i class="ri-external-link-line"></i>
          獨立視窗
        </button>
        <button class="btn btn-secondary btn-sm" @click="refreshSessions">重新整理</button>
        <router-link to="/client/chatdesk/canned-replies" class="btn btn-secondary btn-sm">罐頭回覆</router-link>
        <router-link to="/client/chatdesk/settings" class="btn btn-secondary btn-sm">設定</router-link>
        <router-link to="/client/chatdesk/subscription" class="btn btn-secondary btn-sm">訂閱</router-link>
      </div>
    </div>

    <div v-if="subscriptionLoading" class="center-state">載入訂閱狀態…</div>
    <div v-else-if="!subscribed" class="center-state upgrade">
      <i class="ri-customer-service-2-line"></i>
      <h2>尚未訂閱 ChatDesk</h2>
      <p>訂閱後即可使用真人客服工作台回覆 LINE 客人。</p>
      <router-link to="/client/chatdesk/subscription" class="btn btn-primary">前往訂閱</router-link>
    </div>

    <div v-else class="desk-layout">
      <!-- 左：佇列 -->
      <aside class="pane queue-pane">
        <div class="queue-tabs">
          <button
            type="button"
            :class="{ active: sessionView === 'waiting' }"
            @click="switchSessionView('waiting')"
          >待處理</button>
          <button
            type="button"
            :class="{ active: sessionView === 'active' }"
            @click="switchSessionView('active')"
          >進行中</button>
        </div>
        <div v-if="sessionsLoading" class="pane-empty">載入中…</div>
        <div v-else-if="!sessions.length" class="pane-empty">
          {{ sessionView === 'waiting' ? '目前沒有待處理對話' : '目前沒有進行中對話' }}
        </div>
        <button
          v-for="s in sessions"
          :key="s.sessionId"
          type="button"
          :class="['queue-item', { active: s.sessionId === activeSessionId }]"
          @click="selectSession(s.sessionId)"
        >
          <div class="queue-title">
            <span>{{ s.memberName || s.lineUserId || s.sessionId }}</span>
            <span :class="['mode-badge', s.mode]">{{ modeLabel(s.mode) }}</span>
          </div>
          <div class="queue-preview">{{ s.lastMessagePreview || '—' }}</div>
          <div class="queue-meta">{{ formatTime(s.lastMessageAt) }}</div>
        </button>
      </aside>

      <!-- 中：聊天 -->
      <section class="pane chat-pane">
        <div v-if="!activeSessionId" class="pane-empty">請從左側選擇對話</div>
        <template v-else>
          <div class="chat-header">
            <div>
              <strong>{{ activeSession?.memberName || activeSession?.lineUserId || activeSessionId }}</strong>
              <span :class="['mode-badge', activeSession?.mode]">{{ modeLabel(activeSession?.mode) }}</span>
              <router-link
                v-if="activeSession?.supportTicketId"
                :to="`/client/support/tickets/${activeSession.supportTicketId}`"
                class="ticket-chip"
                title="開啟關聯工單"
              >
                <i class="ri-ticket-2-line"></i>
                {{ activeSession.supportTicketNumber || '工單' }}
              </router-link>
            </div>
            <div class="chat-actions">
              <button
                v-if="activeSession?.mode === 'waiting_human'"
                class="btn btn-primary btn-sm"
                :disabled="actionLoading"
                @click="claim"
              >接手</button>
              <button
                v-if="activeSession?.mode === 'human' || activeSession?.mode === 'waiting_human'"
                class="btn btn-secondary btn-sm"
                :disabled="actionLoading"
                @click="transfer"
              >轉派</button>
              <button
                v-if="activeSession?.mode === 'human'"
                class="btn btn-secondary btn-sm"
                :disabled="actionLoading"
                @click="release"
              >交回 AI</button>
              <button
                v-if="activeSession && !activeSession.supportTicketId && activeSession.mode !== 'closed'"
                class="btn btn-secondary btn-sm"
                :disabled="actionLoading"
                @click="createTicket"
              >建立工單</button>
              <button
                v-if="activeSession && activeSession.mode !== 'closed'"
                class="btn btn-danger-outline btn-sm"
                :disabled="actionLoading"
                @click="closeSession"
              >結束</button>
            </div>
          </div>

          <div ref="messagesEl" class="messages">
            <div
              v-for="m in messages"
              :key="m.id"
              :class="['msg', m.senderType]"
            >
              <div class="msg-meta">{{ senderLabel(m.senderType) }} · {{ formatTime(m.createdAt) }}</div>
              <div
                v-if="m.messageType === 'line_template' && m.messagePayload?.messages"
                class="line-template-preview"
              >
                <LineChatMessages
                  :messages="toLineChatPreviewMessages(m.messagePayload.messages)"
                  :show-date="false"
                  welcome-text=""
                  empty-text="無法顯示範本內容"
                />
              </div>
              <div
                v-else-if="m.messageType === 'text' && m.messagePayload?.emojis?.length"
                class="msg-body line-inbound-text"
              >
                <template
                  v-for="(segment, segmentIndex) in lineInboundTextSegments(
                    m.content,
                    m.messagePayload.emojis
                  )"
                  :key="segmentIndex"
                >
                  <span v-if="segment.type === 'text'">{{ segment.text }}</span>
                  <img
                    v-else
                    class="line-inbound-emoji"
                    :src="lineEmojiUrl(segment.productId, segment.emojiId)"
                    alt="LINE 表情符號"
                    loading="lazy"
                  >
                </template>
              </div>
              <div
                v-else-if="m.messageType === 'location' && m.messagePayload"
                class="msg-location"
              >
                <div class="location-icon"><i class="ri-map-pin-2-fill"></i></div>
                <div class="location-content">
                  <strong>{{ m.messagePayload.title || '位置資訊' }}</strong>
                  <span v-if="m.messagePayload.address">{{ m.messagePayload.address }}</span>
                  <a
                    v-if="locationMapUrl(m.messagePayload)"
                    :href="locationMapUrl(m.messagePayload)"
                    target="_blank"
                    rel="noopener"
                  >在地圖中開啟</a>
                </div>
              </div>
              <div
                v-else-if="['image', 'sticker'].includes(m.messageType) && m.mediaUrl"
                :class="['msg-media', { 'msg-sticker': m.messageType === 'sticker' }]"
              >
                <img
                  :src="m.mediaUrl"
                  :alt="m.messageType === 'sticker' ? 'LINE 貼圖' : '使用者傳送的圖片'"
                  loading="lazy"
                />
              </div>
              <div v-else-if="m.messageType === 'video' && m.mediaUrl" class="msg-video">
                <video controls preload="metadata" playsinline>
                  <source
                    :src="m.mediaUrl"
                    :type="m.mimeType?.startsWith('video/') ? m.mimeType : 'video/mp4'"
                  >
                  瀏覽器不支援影片播放
                </video>
                <a :href="m.mediaUrl" target="_blank" rel="noopener">無法播放時開啟原始影片</a>
              </div>
              <div v-else-if="m.messageType === 'audio' && m.mediaUrl" class="msg-audio">
                <audio :src="m.mediaUrl" controls preload="metadata">瀏覽器不支援聲音播放</audio>
              </div>
              <div v-else-if="m.messageType === 'file' && m.mediaUrl" class="msg-file">
                <a :href="m.mediaUrl" target="_blank" rel="noopener" download>
                  📎 {{ m.fileName || '下載檔案' }}
                </a>
              </div>
              <div v-else class="msg-body">{{ m.content }}</div>
              <div
                v-if="
                  !['text', 'sticker', 'audio', 'video', 'file'].includes(m.messageType)
                  && m.content
                  && m.mediaUrl
                "
                class="msg-body msg-caption"
              >{{ m.content }}</div>
            </div>
          </div>

          <div class="composer">
            <div class="composer-tools">
              <button type="button" class="tool-btn" title="表情" @click="toggleEmoji">😀</button>
              <label class="tool-btn" title="圖片">
                🖼
                <input type="file" accept="image/*" hidden @change="onPickImage" />
              </label>
              <label class="tool-btn" title="檔案">
                📎
                <input type="file" hidden @change="onPickFile" />
              </label>
              <button
                type="button"
                class="tool-btn canned-tool-btn"
                title="罐頭回覆"
                :disabled="activeSession?.mode !== 'human'"
                @click="toggleCannedReplies"
              >💬 罐頭</button>
            </div>
            <div v-if="showEmoji" class="emoji-panel">
              <button
                v-for="e in emojiList"
                :key="e"
                type="button"
                class="emoji-item"
                @click="insertEmoji(e)"
              >{{ e }}</button>
            </div>
            <div v-if="showCannedReplies" class="canned-panel">
              <input
                v-model.trim="cannedReplySearch"
                class="canned-search"
                type="search"
                placeholder="搜尋罐頭回覆"
              >
              <div v-if="cannedRepliesLoading" class="canned-empty">載入中...</div>
              <div v-else-if="filteredCannedReplies.length === 0" class="canned-empty">
                沒有符合的罐頭回覆
              </div>
              <template v-else>
                <div
                  v-for="group in cannedReplyGroups"
                  :key="group.scope"
                  class="canned-group"
                >
                  <div v-if="group.items.length" class="canned-group-title">{{ group.label }}</div>
                  <button
                    v-for="reply in group.items"
                    :key="reply.id"
                    type="button"
                    class="canned-item"
                    @click="applyCannedReply(reply)"
                  >
                    <strong>{{ reply.title }}</strong>
                    <span>{{ cannedReplyPreview(reply) }}</span>
                  </button>
                </div>
              </template>
            </div>
            <div v-if="pendingMedia" class="pending-media">
              待傳送：{{ pendingMedia.fileName || pendingMedia.messageType }}
              <button type="button" @click="pendingMedia = null">移除</button>
            </div>
            <textarea
              v-model="draft"
              rows="3"
              placeholder="輸入回覆…（需先接手）"
              :disabled="activeSession?.mode !== 'human' || sending"
              @keydown.enter.exact="onComposerEnter"
            />
            <button
              class="btn btn-primary send-btn"
              :disabled="activeSession?.mode !== 'human' || sending || (!draft.trim() && !pendingMedia)"
              @click="sendReply"
            >發送</button>
          </div>
        </template>
      </section>

      <!-- 右：會員情境 -->
      <aside class="pane context-pane">
        <div class="pane-header">
          會員情境
          <button v-if="activeSessionId" class="link-btn" @click="loadMemberContext">重新整理</button>
        </div>
        <div v-if="!activeSessionId" class="pane-empty">選擇對話後顯示</div>
        <div v-else-if="contextLoading" class="pane-empty">載入中…</div>
        <div v-else class="context-body">
          <input
            v-model="historyKeyword"
            class="history-search"
            type="search"
            placeholder="搜尋 AI 與客服對話摘要"
          />
          <div class="ctx-block">
            <h3>AI 對話歷史</h3>
            <div v-if="!filteredAiHistory.length" class="muted">
              {{ historyKeyword.trim() ? '沒有符合的 AI 對話' : '尚無 AI 對話' }}
            </div>
            <div v-for="item in filteredAiHistory" :key="item.sessionId" class="history-card">
              <button type="button" class="history-item" @click="toggleHistory('ai', item.sessionId)">
                <span class="history-preview">{{ item.preview || '（無內容）' }}</span>
                <span class="history-meta">
                  {{ item.turnCount }} 則 · {{ formatTime(item.lastMessageAt) }}
                  <span v-if="item.isCurrent"> · 目前</span>
                  <span v-if="item.handoffRequired"> · 曾轉接</span>
                </span>
              </button>
              <div v-if="expandedHistoryKey === historyKey('ai', item.sessionId)" class="history-transcript">
                <div v-if="historyDetailLoading" class="muted">載入中…</div>
                <div v-else-if="!historyTurns.length" class="muted">這段對話沒有內容</div>
                <div
                  v-for="turn in historyTurns"
                  :key="turn.id"
                  class="history-turn"
                  :class="turn.role"
                >
                  <div class="history-turn-meta">{{ historyRoleLabel(turn) }} · {{ formatTime(turn.createdAt) }}</div>
                  <div class="history-turn-text">{{ turn.content }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="ctx-block">
            <h3>客服歷史</h3>
            <div v-if="!filteredServiceHistory.length" class="muted">
              {{ historyKeyword.trim() ? '沒有符合的客服紀錄' : '尚無客服紀錄' }}
            </div>
            <div v-for="item in filteredServiceHistory" :key="item.sessionId" class="history-card">
              <button type="button" class="history-item" @click="toggleHistory('service', item.sessionId)">
                <span class="history-title">
                  <span :class="['mode-badge', item.mode]">{{ modeLabel(item.mode) }}</span>
                  <span v-if="item.agentName">{{ item.agentName }}</span>
                  <span v-if="item.isCurrent" class="history-current">目前</span>
                </span>
                <span class="history-preview">{{ item.preview || '（無內容）' }}</span>
                <span class="history-meta">
                  {{ item.messageCount }} 則 · {{ formatTime(item.lastMessageAt || item.startedAt) }}
                  <span v-if="item.supportTicketNumber"> · {{ item.supportTicketNumber }}</span>
                </span>
              </button>
              <div v-if="expandedHistoryKey === historyKey('service', item.sessionId)" class="history-transcript">
                <button
                  v-if="!item.isCurrent"
                  type="button"
                  class="link-btn history-open"
                  @click="selectSession(item.sessionId)"
                >在工作台開啟</button>
                <div v-if="historyDetailLoading" class="muted">載入中…</div>
                <div v-else-if="!historyTurns.length" class="muted">這段對話沒有內容</div>
                <div
                  v-for="turn in historyTurns"
                  :key="turn.id"
                  class="history-turn"
                  :class="turn.role"
                >
                  <div class="history-turn-meta">{{ historyRoleLabel(turn) }} · {{ formatTime(turn.createdAt) }}</div>
                  <div class="history-turn-text">{{ turn.content }}</div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!memberContext?.member" class="ctx-block">
            <p class="muted unbound-note">尚未綁定會員，無法顯示基本資料與交易紀錄。</p>
          </div>
          <template v-else>
          <div class="ctx-block">
            <h3>客服工單</h3>
            <template v-if="activeSession?.supportTicketId">
              <p>
                <router-link
                  :to="`/client/support/tickets/${activeSession.supportTicketId}`"
                  class="link"
                >
                  {{ activeSession.supportTicketNumber || activeSession.supportTicketId }}
                </router-link>
              </p>
              <p class="muted">狀態：{{ activeSession.supportTicketStatus || '—' }}</p>
            </template>
            <template v-else>
              <p class="muted">尚未關聯工單</p>
              <button
                v-if="activeSession?.mode !== 'closed'"
                class="btn btn-primary btn-sm"
                :disabled="actionLoading"
                @click="createTicket"
              >建立工單</button>
            </template>
          </div>
          <div class="ctx-block">
            <h3>基本資料</h3>
            <p><strong>{{ memberContext.member.name || '—' }}</strong></p>
            <p>手機：{{ memberContext.member.phone || '—' }}</p>
            <p>Email：{{ memberContext.member.email || '—' }}</p>
            <p>點數：{{ memberContext.pointsSummary?.currentPoints ?? 0 }}</p>
            <router-link
              v-if="memberContext.member.id"
              :to="`/client/members/${memberContext.member.id}`"
              class="link"
            >開啟會員頁</router-link>
          </div>
          <div class="ctx-block">
            <h3>近期訂單</h3>
            <div v-if="!memberContext.recentOrders?.length" class="muted">無訂單</div>
            <button
              v-for="o in memberContext.recentOrders"
              :key="o.id"
              type="button"
              class="order-row"
              @click="openOrderModal(o)"
            >
              <span>{{ o.orderNumber }}</span>
              <span>{{ o.status }}</span>
              <span>NT$ {{ formatMoney(o.totalAmount) }}</span>
            </button>
          </div>
          <div class="ctx-block">
            <h3>近期點數異動</h3>
            <div v-if="!memberContext.recentPointTransactions?.length" class="muted">無異動</div>
            <div
              v-for="t in memberContext.recentPointTransactions"
              :key="t.id"
              class="point-row"
            >
              <span>{{ t.transactionType }}</span>
              <span>{{ t.points }}</span>
              <span>{{ formatTime(t.createdAt) }}</span>
            </div>
          </div>
          </template>
        </div>
      </aside>
    </div>

    <!-- 訂單 Modal -->
    <div v-if="orderModal" class="modal-mask" @click.self="orderModal = null">
      <div class="modal-card">
        <h3>訂單詳情</h3>
        <p>訂單編號：{{ orderModal.orderNumber }}</p>
        <p>狀態：{{ orderModal.status }}</p>
        <p>金額：NT$ {{ formatMoney(orderModal.totalAmount) }}</p>
        <p>建立時間：{{ formatTime(orderModal.createdAt) }}</p>
        <div class="modal-actions">
          <router-link
            :to="`/client/orders/${orderModal.id}`"
            class="btn btn-primary"
            target="_blank"
          >在訂單頁開啟</router-link>
          <button class="btn btn-secondary" @click="orderModal = null">關閉</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { showError, showSuccess, showConfirm } from '@/utils/swal.js';
import { chatdeskService, getChatDeskDeviceId } from '@/api/modules/chatdesk.js';
import { toComposerPayload } from '@/utils/chatdeskCannedReply.js';
import { toLineChatPreviewMessages } from '@/utils/lineApiMessagePreview.js';
import LineChatMessages from '@/components/LineChatMessages.vue';

const WS_URL = (process.env.VUE_APP_CHATDESK_WS_URL || '').trim();
const wsUrlConfigured = Boolean(WS_URL);

export default {
  name: 'ChatDeskDesk',
  components: { LineChatMessages },
  setup() {
    usePageTitle('ChatDesk 工作台');
    const route = useRoute();
    const router = useRouter();

    const deskRoot = ref(null);
    const isFullscreen = ref(false);
    const subscriptionLoading = ref(true);
    const subscribed = ref(false);
    const sessionsLoading = ref(false);
    const sessions = ref([]);
    const sessionView = ref('waiting');
    const activeSessionId = ref(null);
    const activeSession = ref(null);
    const messages = ref([]);
    const messagesEl = ref(null);
    const draft = ref('');
    const sending = ref(false);
    const actionLoading = ref(false);
    const contextLoading = ref(false);
    const memberContext = ref(null);
    const historyKeyword = ref('');
    const expandedHistoryKey = ref(null);
    const historyDetailLoading = ref(false);
    const historyTurns = ref([]);
    let historyCache = {};
    const orderModal = ref(null);
    const showEmoji = ref(false);
    const showCannedReplies = ref(false);
    const cannedRepliesLoading = ref(false);
    const cannedReplySearch = ref('');
    const cannedReplies = ref([]);
    const pendingMedia = ref(null);
    const wsConnected = ref(false);
    const wsSessionConflict = ref(false);
    const emojiList = ['😀', '😊', '👍', '🙏', '❤️', '🎉', '😅', '😢', '👏', '💯'];

    let ws = null;
    let wsRetry = 0;
    let wsTimer = null;
    let wsHeartbeat = null;
    let wsDisposed = false;
    let messageCursor = null;
    const chatDeskDeviceId = getChatDeskDeviceId();

    const syncFullscreenState = () => {
      isFullscreen.value = document.fullscreenElement === deskRoot.value;
    };

    const toggleFullscreen = async () => {
      try {
        if (document.fullscreenElement === deskRoot.value) {
          await document.exitFullscreen();
        } else {
          await deskRoot.value?.requestFullscreen();
        }
      } catch (error) {
        await showError('無法切換全螢幕', error.message || '瀏覽器不支援全螢幕模式');
      }
    };

    const openStandaloneWindow = async () => {
      const target = router.resolve({ name: 'ChatDeskDesk' }).href;
      const popup = window.open(
        target,
        'chatdesk-workbench',
        'popup=yes,width=1440,height=900,resizable=yes,scrollbars=yes'
      );
      if (!popup) {
        await showError('無法開啟獨立視窗', '請允許瀏覽器開啟彈出式視窗後再試一次');
      }
    };

    const modeLabel = (mode) => ({
      waiting_human: '待接手',
      human: '服務中',
      ai: 'AI',
      closed: '已結束'
    }[mode] || mode);

    const senderLabel = (t) => ({
      user: '客人',
      ai: 'AI',
      agent: '客服',
      system: '系統'
    }[t] || t);

    const formatTime = (v) => {
      if (!v) return '—';
      try {
        return new Date(v).toLocaleString('zh-TW');
      } catch (_) {
        return String(v);
      }
    };

    const formatMoney = (v) => {
      const n = parseFloat(v || 0);
      return n.toLocaleString('zh-TW');
    };

    const locationMapUrl = (payload) => {
      const latitude = Number(payload?.latitude);
      const longitude = Number(payload?.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return '';
      return `https://www.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}`;
    };

    const lineEmojiUrl = (productId, emojiId) => {
      const normalizedEmojiId = String(emojiId || '').padStart(3, '0');
      return `https://stickershop.line-scdn.net/sticonshop/v1/sticon/${productId}/android/${normalizedEmojiId}.png`;
    };

    const lineInboundTextSegments = (text, emojis) => {
      const value = String(text || '');
      const normalized = (emojis || [])
        .filter(
          emoji => Number.isInteger(emoji?.index)
            && emoji.productId
            && emoji.emojiId != null
        )
        .sort((a, b) => a.index - b.index);
      if (!normalized.length) return [{ type: 'text', text: value }];

      const segments = [];
      let cursor = 0;
      for (const emoji of normalized) {
        if (emoji.index < cursor || emoji.index > value.length) continue;
        if (emoji.index > cursor) {
          segments.push({ type: 'text', text: value.slice(cursor, emoji.index) });
        }
        segments.push({
          type: 'emoji',
          productId: String(emoji.productId),
          emojiId: String(emoji.emojiId)
        });
        const length = Number.isInteger(emoji.length) && emoji.length > 0 ? emoji.length : 1;
        cursor = emoji.index + length;
      }
      if (cursor < value.length) {
        segments.push({ type: 'text', text: value.slice(cursor) });
      }
      return segments;
    };

    const filteredCannedReplies = computed(() => {
      const keyword = cannedReplySearch.value.toLowerCase();
      if (!keyword) return cannedReplies.value;
      return cannedReplies.value.filter(item =>
        item.title?.toLowerCase().includes(keyword)
        || item.content?.toLowerCase().includes(keyword)
        || item.fileName?.toLowerCase().includes(keyword)
      );
    });

    const cannedReplyGroups = computed(() => [
      {
        scope: 'team',
        label: '團隊共用',
        items: filteredCannedReplies.value.filter(item => item.scope === 'team')
      },
      {
        scope: 'personal',
        label: '我的個人罐頭',
        items: filteredCannedReplies.value.filter(item => item.scope === 'personal')
      }
    ]);

    const scrollToBottom = async () => {
      await nextTick();
      if (messagesEl.value) {
        messagesEl.value.scrollTop = messagesEl.value.scrollHeight;
      }
    };

    const checkSubscription = async () => {
      subscriptionLoading.value = true;
      try {
        const res = await chatdeskService.getSubscription();
        subscribed.value = !!res?.data?.subscribed;
      } catch (err) {
        if (err.response?.status === 403) {
          subscribed.value = false;
        } else {
          await showError('載入失敗', err.response?.data?.message || err.message);
        }
      } finally {
        subscriptionLoading.value = false;
      }
    };

    const refreshSessions = async () => {
      if (!subscribed.value) return;
      sessionsLoading.value = true;
      try {
        const params = { pageSize: 50 };
        params.mode = sessionView.value === 'waiting' ? 'waiting_human' : 'human';
        const res = await chatdeskService.listSessions(params);
        sessions.value = res?.data?.items || [];
      } catch (err) {
        await showError('佇列載入失敗', err.response?.data?.message || err.message);
      } finally {
        sessionsLoading.value = false;
      }
    };

    const switchSessionView = async (view) => {
      if (sessionView.value === view) return;
      sessionView.value = view;
      activeSessionId.value = null;
      activeSession.value = null;
      messages.value = [];
      memberContext.value = null;
      messageCursor = null;
      await refreshSessions();
    };

    const loadSessionDetail = async (sessionId) => {
      const res = await chatdeskService.getSession(sessionId);
      activeSession.value = res?.data?.session || null;
      messages.value = res?.data?.messages || [];
      if (messages.value.length) {
        messageCursor = messages.value[messages.value.length - 1].createdAt;
      }
      await scrollToBottom();
    };

    const resetHistoryView = () => {
      historyCache = {};
      historyKeyword.value = '';
      expandedHistoryKey.value = null;
      historyTurns.value = [];
      historyDetailLoading.value = false;
    };

    const historyKey = (type, sessionId) => `${type}:${sessionId}`;

    const matchesHistoryKeyword = (item) => {
      const keyword = historyKeyword.value.trim().toLowerCase();
      if (!keyword) return true;
      const haystack = [
        item.preview,
        item.agentName,
        item.supportTicketNumber,
        item.mode
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(keyword);
    };

    const filteredAiHistory = computed(() => (
      (memberContext.value?.aiHistory || []).filter(matchesHistoryKeyword)
    ));

    const filteredServiceHistory = computed(() => (
      (memberContext.value?.serviceHistory || []).filter((item) => (
        item.mode !== 'waiting_human' && matchesHistoryKeyword(item)
      ))
    ));

    const historyRoleLabel = (turn) => {
      if (turn.role === 'agent' && turn.agentName) return `客服 ${turn.agentName}`;
      return senderLabel(turn.role);
    };

    const loadMemberContext = async () => {
      resetHistoryView();
      if (!activeSessionId.value) {
        memberContext.value = null;
        return;
      }
      contextLoading.value = true;
      memberContext.value = null;
      try {
        const res = await chatdeskService.getMemberContext(activeSessionId.value);
        memberContext.value = res?.data || null;
      } catch (err) {
        console.warn(err);
        memberContext.value = { member: null, aiHistory: [], serviceHistory: [] };
      } finally {
        contextLoading.value = false;
      }
    };

    const toggleHistory = async (type, historySessionId) => {
      const key = historyKey(type, historySessionId);
      if (expandedHistoryKey.value === key) {
        expandedHistoryKey.value = null;
        return;
      }
      expandedHistoryKey.value = key;
      if (historyCache[key]) {
        historyTurns.value = historyCache[key];
        return;
      }
      historyDetailLoading.value = true;
      historyTurns.value = [];
      try {
        const res = await chatdeskService.getConsumerHistory(activeSessionId.value, {
          historyType: type,
          historySessionId
        });
        const turns = res?.data?.turns || [];
        historyCache[key] = turns;
        if (expandedHistoryKey.value === key) historyTurns.value = turns;
      } catch (err) {
        if (expandedHistoryKey.value === key) expandedHistoryKey.value = null;
        await showError('歷史對話載入失敗', err.response?.data?.message || err.message);
      } finally {
        historyDetailLoading.value = false;
      }
    };

    const selectSession = async (sessionId) => {
      activeSessionId.value = sessionId;
      try {
        await loadSessionDetail(sessionId);
        await loadMemberContext();
      } catch (err) {
        await showError('載入對話失敗', err.response?.data?.message || err.message);
      }
    };

    const claim = async () => {
      actionLoading.value = true;
      try {
        await chatdeskService.claimSession(activeSessionId.value);
        await showSuccess('已接手');
        await loadSessionDetail(activeSessionId.value);
        await refreshSessions();
      } catch (err) {
        await showError('接手失敗', err.response?.data?.message || err.message);
        await refreshSessions();
        if (activeSessionId.value) await loadSessionDetail(activeSessionId.value);
      } finally {
        actionLoading.value = false;
      }
    };

    const transfer = async () => {
      const toAccountId = window.prompt('請輸入轉派對象的帳號 UUID');
      if (!toAccountId?.trim()) return;
      actionLoading.value = true;
      try {
        await chatdeskService.transferSession(activeSessionId.value, toAccountId.trim());
        await showSuccess('已轉派');
        await loadSessionDetail(activeSessionId.value);
        await refreshSessions();
      } catch (err) {
        await showError('轉派失敗', err.response?.data?.message || err.message);
      } finally {
        actionLoading.value = false;
      }
    };

    const release = async () => {
      const ok = await showConfirm('交回 AI', '確定將對話交回 AI 嗎？');
      if (!ok) return;
      actionLoading.value = true;
      try {
        await chatdeskService.releaseSession(activeSessionId.value);
        await loadSessionDetail(activeSessionId.value);
        await refreshSessions();
      } catch (err) {
        await showError('操作失敗', err.response?.data?.message || err.message);
      } finally {
        actionLoading.value = false;
      }
    };

    const loadAssignableAgents = async () => {
      try {
        const response = await chatdeskService.listAssignableAgents();
        return response?.data || [];
      } catch (err) {
        await showError('載入客服人員失敗', err.response?.data?.message || err.message);
        return null;
      }
    };

    const populateAssigneeSelect = (selectId, agents) => {
      const select = document.getElementById(selectId);
      if (!select) return;
      agents.forEach((agent) => {
        const primary = agent.name || agent.username || agent.email || agent.id;
        const secondary = agent.name && (agent.username || agent.email);
        const label = secondary ? `${primary}（${secondary}）` : primary;
        const option = new Option(label, agent.id, agent.isCurrent, agent.isCurrent);
        select.add(option);
      });
    };

    const promptTicketDetails = async () => {
      const agents = await loadAssignableAgents();
      if (agents === null) return null;
      const result = await Swal.fire({
        icon: 'question',
        title: '建立工單',
        html: `
          <p style="margin:0 0 16px;text-align:left;">對話摘要會自動附加到工單內容。</p>
          <label for="chatdesk-ticket-assignee" style="display:block;text-align:left;">
            指派負責人
          </label>
          <select
            id="chatdesk-ticket-assignee"
            style="width:100%;margin-top:6px;padding:8px;border:1px solid #d1d5db;border-radius:6px;"
          ></select>
          <label for="chatdesk-ticket-work-description" style="display:block;margin-top:14px;text-align:left;">
            待辦工作說明
          </label>
          <textarea
            id="chatdesk-ticket-work-description"
            maxlength="5000"
            placeholder="例如：確認商品照片並安排補寄"
            style="width:100%;min-height:100px;margin-top:6px;padding:8px;border:1px solid #d1d5db;border-radius:6px;"
          ></textarea>
        `,
        showCancelButton: true,
        confirmButtonText: '建立',
        cancelButtonText: '取消',
        confirmButtonColor: '#1976d2',
        cancelButtonColor: '#6c757d',
        reverseButtons: true,
        allowOutsideClick: false,
        didOpen: () => populateAssigneeSelect('chatdesk-ticket-assignee', agents),
        preConfirm: () => ({
          assignedTo: document.getElementById('chatdesk-ticket-assignee')?.value || null,
          workDescription: String(
            document.getElementById('chatdesk-ticket-work-description')?.value || ''
          ).trim()
        })
      });
      return result.isConfirmed ? result.value : null;
    };

    const createTicket = async () => {
      if (!activeSessionId.value) return;
      if (activeSession.value?.supportTicketId) {
        await showSuccess('已關聯工單', activeSession.value.supportTicketNumber || '');
        return;
      }
      const ticketDetails = await promptTicketDetails();
      if (ticketDetails === null) return;
      actionLoading.value = true;
      try {
        const res = await chatdeskService.createTicket(activeSessionId.value, ticketDetails);
        const ticket = res?.data?.ticket;
        await showSuccess(
          res?.data?.alreadyLinked ? '已關聯工單' : '工單已建立',
          ticket?.ticketNumber || ''
        );
        await loadSessionDetail(activeSessionId.value);
        await refreshSessions();
      } catch (err) {
        await showError('建立工單失敗', err.response?.data?.message || err.message);
      } finally {
        actionLoading.value = false;
      }
    };

    const closeSession = async () => {
      let createTicketOnClose = false;
      let ticketWorkDescription = '';
      let ticketAssigneeId = null;

      if (activeSession.value && !activeSession.value.supportTicketId) {
        const agents = await loadAssignableAgents();
        if (agents === null) return;
        const result = await Swal.fire({
          icon: 'warning',
          title: '結束對話',
          html: `
            <p style="margin:0 0 12px;text-align:left;">確定結束此對話嗎？結束後，這位客人的後續訊息會回到 AI。</p>
            <label style="display:flex;align-items:center;gap:8px;text-align:left;cursor:pointer;">
              <input type="checkbox" id="chatdesk-close-create-ticket" checked />
              <span>同時建立客服工單</span>
            </label>
            <label for="chatdesk-close-assignee" style="display:block;margin-top:12px;text-align:left;">
              指派負責人
            </label>
            <select
              id="chatdesk-close-assignee"
              style="width:100%;margin-top:6px;padding:8px;border:1px solid #d1d5db;border-radius:6px;"
            ></select>
            <label for="chatdesk-close-work-description" style="display:block;margin-top:12px;text-align:left;">
              待辦工作說明
            </label>
            <textarea
              id="chatdesk-close-work-description"
              maxlength="5000"
              placeholder="例如：確認商品照片並安排補寄"
              style="width:100%;min-height:90px;margin-top:6px;padding:8px;border:1px solid #d1d5db;border-radius:6px;"
            ></textarea>
          `,
          showCancelButton: true,
          confirmButtonText: '結束',
          cancelButtonText: '取消',
          confirmButtonColor: '#dc3545',
          cancelButtonColor: '#6c757d',
          reverseButtons: true,
          allowOutsideClick: false,
          didOpen: () => populateAssigneeSelect('chatdesk-close-assignee', agents),
          preConfirm: () => {
            const el = document.getElementById('chatdesk-close-create-ticket');
            const assigneeEl = document.getElementById('chatdesk-close-assignee');
            const descriptionEl = document.getElementById('chatdesk-close-work-description');
            return {
              createTicket: !!el?.checked,
              assignedTo: assigneeEl?.value || null,
              workDescription: String(descriptionEl?.value || '').trim()
            };
          }
        });
        if (!result.isConfirmed) return;
        createTicketOnClose = !!result.value?.createTicket;
        ticketAssigneeId = result.value?.assignedTo || null;
        ticketWorkDescription = result.value?.workDescription || '';
      } else {
        const ok = await showConfirm('結束對話', '確定結束此對話嗎？結束後，這位客人的後續訊息會回到 AI。');
        if (!ok) return;
      }

      actionLoading.value = true;
      try {
        if (createTicketOnClose) {
          await chatdeskService.createTicket(activeSessionId.value, {
            assignedTo: ticketAssigneeId,
            workDescription: ticketWorkDescription
          });
        }
        await chatdeskService.closeSession(activeSessionId.value);
        activeSessionId.value = null;
        activeSession.value = null;
        messages.value = [];
        memberContext.value = null;
        await refreshSessions();
      } catch (err) {
        await showError('操作失敗', err.response?.data?.message || err.message);
      } finally {
        actionLoading.value = false;
      }
    };

    const sendReply = async () => {
      if (sending.value || activeSession.value?.mode !== 'human') return;
      const payload = pendingMedia.value
        ? { ...pendingMedia.value, content: draft.value.trim() || null }
        : { messageType: 'text', content: draft.value };
      if (payload.messageType === 'text' && !payload.content?.trim()) return;

      sending.value = true;
      try {
        await chatdeskService.replySession(activeSessionId.value, payload);
        draft.value = '';
        pendingMedia.value = null;
        showEmoji.value = false;
        showCannedReplies.value = false;
        await loadSessionDetail(activeSessionId.value);
        await refreshSessions();
      } catch (err) {
        await showError('發送失敗', err.response?.data?.message || err.message);
      } finally {
        sending.value = false;
      }
    };

    const onComposerEnter = (event) => {
      // 中文／日文輸入法按 Enter 是完成組字，不可在 composition 尚未結束時送出。
      if (event.isComposing || event.keyCode === 229) return;
      event.preventDefault();
      sendReply();
    };

    const toggleEmoji = () => {
      showEmoji.value = !showEmoji.value;
      if (showEmoji.value) showCannedReplies.value = false;
    };
    const insertEmoji = (e) => { draft.value = `${draft.value || ''}${e}`; };

    const loadCannedReplies = async () => {
      cannedRepliesLoading.value = true;
      try {
        const response = await chatdeskService.listCannedReplies({ activeOnly: true });
        cannedReplies.value = response?.data?.items || [];
      } catch (err) {
        await showError('罐頭回覆載入失敗', err.response?.data?.message || err.message);
      } finally {
        cannedRepliesLoading.value = false;
      }
    };

    const toggleCannedReplies = async () => {
      showCannedReplies.value = !showCannedReplies.value;
      showEmoji.value = false;
      if (showCannedReplies.value) await loadCannedReplies();
    };

    const cannedReplyPreview = (reply) => {
      if (reply.messageType === 'line_template') {
        return `LINE 範本 · ${reply.lineMsgTemplateSnapshot?.name || reply.title}`;
      }
      const prefix = reply.messageType === 'image'
        ? '圖片'
        : (reply.messageType === 'file' ? '檔案' : '文字');
      return `${prefix} · ${reply.content || reply.fileName || '無文字說明'}`;
    };

    const applyCannedReply = async (reply) => {
      if (draft.value.trim() || pendingMedia.value) {
        const confirmed = await showConfirm('取代目前草稿', '套用罐頭回覆會取代目前輸入的文字與附件，是否繼續？');
        if (!confirmed) return;
      }
      const payload = toComposerPayload(reply, memberContext.value);
      draft.value = payload.draft;
      pendingMedia.value = payload.pendingMedia;
      showCannedReplies.value = false;
      cannedReplySearch.value = '';
    };

    const uploadAndSetPending = async (file, messageType) => {
      try {
        const res = await chatdeskService.uploadFile(file);
        const url = res?.data?.fileUrl || res?.data?.url;
        if (!url) throw new Error('上傳未回傳 URL');
        pendingMedia.value = {
          messageType,
          mediaUrl: url,
          fileName: file.name,
          mimeType: file.type,
          fileSize: file.size
        };
      } catch (err) {
        await showError('上傳失敗', err.response?.data?.message || err.message);
      }
    };

    const onPickImage = async (ev) => {
      const file = ev.target.files?.[0];
      ev.target.value = '';
      if (file) await uploadAndSetPending(file, 'image');
    };
    const onPickFile = async (ev) => {
      const file = ev.target.files?.[0];
      ev.target.value = '';
      if (file) await uploadAndSetPending(file, 'file');
    };

    const openOrderModal = (o) => { orderModal.value = o; };

    const getAuthToken = () =>
      localStorage.getItem('auth_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('jwt_token') ||
      localStorage.getItem('accessToken') ||
      '';

    const fillGap = async () => {
      if (!activeSessionId.value || !messageCursor) return;
      try {
        const res = await chatdeskService.getMessages(activeSessionId.value, { since: messageCursor });
        const list = res?.data?.messages || [];
        if (list.length) {
          const ids = new Set(messages.value.map(m => m.id));
          for (const m of list) {
            if (!ids.has(m.id)) messages.value.push(m);
          }
          messageCursor = messages.value[messages.value.length - 1].createdAt;
          await scrollToBottom();
        }
      } catch (_) { /* ignore */ }
    };

    const clearWsHeartbeat = () => {
      if (wsHeartbeat) {
        clearInterval(wsHeartbeat);
        wsHeartbeat = null;
      }
    };

    const connectWs = async () => {
      if (!subscribed.value || wsDisposed) return;
      if (ws?.readyState === WebSocket.OPEN || ws?.readyState === WebSocket.CONNECTING) return;
      const token = getAuthToken();
      if (!token) return;

      try {
        await chatdeskService.acquireRealtimeSession(chatDeskDeviceId);
        wsSessionConflict.value = false;
      } catch (err) {
        wsConnected.value = false;
        wsSessionConflict.value = err.response?.status === 409;
        scheduleReconnect();
        return;
      }

      if (!WS_URL) return;
      const separator = WS_URL.includes('?') ? '&' : '?';
      const url = `${WS_URL}${separator}token=${encodeURIComponent(token)}&deviceId=${encodeURIComponent(chatDeskDeviceId)}`;
      try {
        ws = new WebSocket(url);
      } catch (err) {
        console.warn('WS create failed', err);
        scheduleReconnect();
        return;
      }
      ws.onopen = () => {
        wsConnected.value = true;
        wsSessionConflict.value = false;
        wsRetry = 0;
        clearWsHeartbeat();
        wsHeartbeat = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
        fillGap();
        refreshSessions();
      };
      ws.onclose = () => {
        wsConnected.value = false;
        clearWsHeartbeat();
        ws = null;
        if (!wsDisposed) scheduleReconnect();
      };
      ws.onerror = () => {
        wsConnected.value = false;
      };
      ws.onmessage = async (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === 'inbox.session_upserted' || data.type === 'inbox.session_assigned') {
            await refreshSessions();
            if (data.payload?.session?.sessionId === activeSessionId.value) {
              await loadSessionDetail(activeSessionId.value);
            }
          }
          if (data.type === 'inbox.message_created') {
            if (data.payload?.sessionId === activeSessionId.value && data.payload.message) {
              const m = data.payload.message;
              if (!messages.value.find(x => x.id === m.id)) {
                messages.value.push(m);
                messageCursor = m.createdAt;
                await scrollToBottom();
              }
            }
            await refreshSessions();
          }
        } catch (_) { /* ignore */ }
      };
    };

    const scheduleReconnect = () => {
      if (wsDisposed) return;
      if (wsTimer) clearTimeout(wsTimer);
      const delay = Math.min(30000, 1000 * (2 ** wsRetry));
      wsRetry += 1;
      wsTimer = setTimeout(() => {
        wsTimer = null;
        connectWs();
      }, delay);
    };

    const onVisibility = async () => {
      if (document.visibilityState === 'visible') {
        if (!wsConnected.value) await connectWs();
        if (wsSessionConflict.value) return;
        fillGap();
        refreshSessions();
      }
    };

    onMounted(async () => {
      document.addEventListener('fullscreenchange', syncFullscreenState);
      await checkSubscription();
      if (subscribed.value) {
        await connectWs();
        if (wsSessionConflict.value) {
          document.addEventListener('visibilitychange', onVisibility);
          return;
        }
        await refreshSessions();
        const deepLinkSessionId = route.query.sessionId;
        if (deepLinkSessionId) {
          try {
            await selectSession(String(deepLinkSessionId));
          } catch (_) {
            /* 對話可能已結束或不在佇列，仍嘗試詳情 API */
          }
        }
        document.addEventListener('visibilitychange', onVisibility);
      }
    });

    onBeforeUnmount(() => {
      wsDisposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      if (wsTimer) clearTimeout(wsTimer);
      clearWsHeartbeat();
      if (ws) {
        try { ws.close(); } catch (_) { /* ignore */ }
      }
    });

    watch(activeSessionId, () => {
      draft.value = '';
      pendingMedia.value = null;
      showCannedReplies.value = false;
      cannedReplySearch.value = '';
    });

    return {
      deskRoot,
      isFullscreen,
      subscriptionLoading,
      subscribed,
      sessionsLoading,
      sessions,
      sessionView,
      activeSessionId,
      activeSession,
      messages,
      messagesEl,
      draft,
      sending,
      actionLoading,
      contextLoading,
      memberContext,
      historyKeyword,
      expandedHistoryKey,
      historyDetailLoading,
      historyTurns,
      filteredAiHistory,
      filteredServiceHistory,
      historyKey,
      historyRoleLabel,
      toggleHistory,
      orderModal,
      showEmoji,
      showCannedReplies,
      cannedRepliesLoading,
      cannedReplySearch,
      filteredCannedReplies,
      cannedReplyGroups,
      pendingMedia,
      wsUrlConfigured,
      wsConnected,
      wsSessionConflict,
      emojiList,
      modeLabel,
      senderLabel,
      formatTime,
      formatMoney,
      locationMapUrl,
      lineEmojiUrl,
      lineInboundTextSegments,
      refreshSessions,
      toggleFullscreen,
      openStandaloneWindow,
      switchSessionView,
      selectSession,
      claim,
      transfer,
      release,
      createTicket,
      closeSession,
      sendReply,
      onComposerEnter,
      toggleEmoji,
      insertEmoji,
      toggleCannedReplies,
      applyCannedReply,
      cannedReplyPreview,
      toLineChatPreviewMessages,
      onPickImage,
      onPickFile,
      loadMemberContext,
      openOrderModal
    };
  }
};
</script>

<style scoped>
.desk-root {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  min-height: 560px;
  background: #f4f6f8;
}
.desk-root:fullscreen { width: 100vw; height: 100vh; min-height: 0; }
.desk-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}
.page-title { font-size: 20px; font-weight: 600; margin: 0; }
.topbar-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.ws-banner { color: #c2410c; font-size: 13px; }
.ws-banner-error { color: #b91c1c; font-weight: 600; }
.ws-banner-warn { color: #a16207; }
.ws-ok { color: #15803d; font-size: 13px; }
.center-state {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 12px; color: #555;
}
.center-state.upgrade i { font-size: 48px; color: #1976d2; }
.desk-layout {
  flex: 1; display: grid;
  grid-template-columns: 280px 1fr 340px;
  min-height: 0;
}
.pane {
  background: #fff; border-right: 1px solid #e5e7eb;
  display: flex; flex-direction: column; min-height: 0;
}
.context-pane { border-right: none; border-left: 1px solid #e5e7eb; }
.pane-header {
  padding: 12px 14px; font-weight: 600; border-bottom: 1px solid #eee;
  display: flex; justify-content: space-between; align-items: center;
}
.queue-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 1px solid #eee;
}
.queue-tabs button {
  border: none;
  border-bottom: 2px solid transparent;
  background: #fff;
  padding: 11px 8px;
  color: #667085;
  cursor: pointer;
}
.queue-tabs button.active {
  border-bottom-color: #1976d2;
  color: #1976d2;
  font-weight: 600;
}
.pane-empty { padding: 24px; color: #888; text-align: center; }
.pane-empty.unbound { color: #b45309; }
.queue-item {
  text-align: left; border: none; border-bottom: 1px solid #f0f0f0;
  background: #fff; padding: 12px 14px; cursor: pointer;
}
.queue-item:hover { background: #f8fafc; }
.queue-item.active { background: #e3f2fd; }
.queue-title { display: flex; justify-content: space-between; gap: 8px; font-weight: 600; }
.queue-preview {
  font-size: 13px; color: #666; margin-top: 4px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.queue-meta { font-size: 12px; color: #999; margin-top: 4px; }
.mode-badge {
  font-size: 11px; padding: 2px 6px; border-radius: 4px; font-weight: 500;
}
.mode-badge.waiting_human { background: #fff7ed; color: #c2410c; }
.mode-badge.human { background: #ecfdf5; color: #047857; }
.mode-badge.ai { background: #eff6ff; color: #1d4ed8; }
.mode-badge.closed { background: #f3f4f6; color: #6b7280; }
.ticket-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e7f5ff;
  color: #1971c2;
  font-size: 12px;
  text-decoration: none;
}
.ticket-chip:hover {
  background: #d0ebff;
}
.chat-header {
  padding: 12px 14px; border-bottom: 1px solid #eee;
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
}
.chat-actions { display: flex; gap: 6px; flex-wrap: wrap; }
.messages {
  flex: 1; overflow-y: auto; padding: 16px; background: #f8fafc;
}
.msg { max-width: 80%; margin-bottom: 12px; }
.msg.user { margin-right: auto; }
.msg.agent, .msg.ai { margin-left: auto; }
.msg.agent {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.msg.system { margin: 8px auto; max-width: 90%; text-align: center; }
.msg-meta { font-size: 11px; color: #999; margin-bottom: 4px; }
.msg-body {
  background: #fff; border: 1px solid #e5e7eb; border-radius: 10px;
  padding: 10px 12px; white-space: pre-wrap; word-break: break-word;
}
.msg.agent .msg-body { background: #dbeafe; border-color: #bfdbfe; }
.msg.ai .msg-body { background: #f3e8ff; border-color: #e9d5ff; }
.msg.system .msg-body { background: #f3f4f6; color: #666; font-size: 13px; }
.line-inbound-text { line-height: 1.5; }
.line-inbound-emoji {
  display: inline-block;
  width: 26px;
  height: 26px;
  margin: 0 1px;
  object-fit: contain;
  vertical-align: text-bottom;
}
.msg-media img { display: block; max-width: 220px; max-height: 320px; border-radius: 8px; }
.msg-sticker img { width: 140px; max-height: 140px; object-fit: contain; }
.msg-video video { display: block; width: min(320px, 100%); max-height: 420px; border-radius: 8px; background: #000; }
.msg-video a { display: inline-block; margin-top: 5px; color: #2563eb; font-size: 12px; text-decoration: none; }
.msg-audio audio { display: block; width: min(320px, 100%); }
.msg-location {
  display: flex;
  width: min(300px, 100%);
  gap: 10px;
  padding: 12px;
  border: 1px solid #d8e0e8;
  border-radius: 10px;
  background: #fff;
}
.location-icon {
  display: flex;
  flex: 0 0 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e8f5e9;
  color: #16833b;
  font-size: 21px;
}
.location-content { display: grid; min-width: 0; gap: 4px; }
.location-content strong { color: #25313c; font-size: 14px; }
.location-content span { color: #65717d; font-size: 12px; word-break: break-word; }
.location-content a { color: #2563eb; font-size: 12px; text-decoration: none; }
.msg-file a {
  display: inline-block; padding: 10px 12px; border: 1px solid #e5e7eb;
  border-radius: 10px; background: #fff; color: #2563eb; text-decoration: none;
  word-break: break-all;
}
.msg.agent .msg-file a { background: #dbeafe; border-color: #bfdbfe; }
.msg-caption { margin-top: 4px; }
.line-template-preview {
  width: min(340px, 100%);
}
.composer {
  border-top: 1px solid #eee; padding: 10px 12px; display: grid;
  grid-template-columns: 1fr auto; gap: 8px; align-items: end;
}
.composer-tools { grid-column: 1 / -1; display: flex; gap: 6px; }
.tool-btn {
  border: 1px solid #e5e7eb; background: #fff; border-radius: 6px;
  padding: 4px 8px; cursor: pointer;
}
.canned-tool-btn { margin-left: auto; }
.emoji-panel {
  grid-column: 1 / -1; display: flex; flex-wrap: wrap; gap: 4px;
  background: #f9fafb; padding: 8px; border-radius: 6px;
}
.emoji-item { border: none; background: transparent; font-size: 20px; cursor: pointer; }
.canned-panel {
  grid-column: 1 / -1;
  max-height: 280px;
  overflow-y: auto;
  display: grid;
  gap: 8px;
  padding: 10px;
  border: 1px solid #dbe3ea;
  border-radius: 8px;
  background: #fff;
}
.canned-search {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 10px;
}
.canned-group { display: grid; gap: 5px; }
.canned-group-title { color: #667085; font-size: 12px; font-weight: 600; padding-top: 3px; }
.canned-item {
  display: grid;
  gap: 2px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e6eaf0;
  border-radius: 6px;
  background: #f9fafb;
  text-align: left;
  cursor: pointer;
}
.canned-item:hover { border-color: #90caf9; background: #eff6ff; }
.canned-item strong { font-size: 13px; }
.canned-item span {
  color: #6b7280;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.canned-empty { color: #7a8490; padding: 12px; text-align: center; font-size: 13px; }
.pending-media {
  grid-column: 1 / -1; font-size: 13px; color: #1d4ed8;
  display: flex; gap: 8px; align-items: center;
}
.composer textarea {
  width: 100%; resize: none; border: 1px solid #d1d5db; border-radius: 8px;
  padding: 10px; font: inherit;
}
.send-btn { height: 40px; }
.context-body { overflow-y: auto; padding: 12px; }
.ctx-block { margin-bottom: 18px; }
.ctx-block h3 { font-size: 14px; margin: 0 0 8px; }
.ctx-block p { margin: 4px 0; font-size: 13px; color: #333; }
.unbound-note { color: #b45309; }
.history-search {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 8px;
  margin-bottom: 8px;
  font: inherit;
  font-size: 12px;
}
.history-card { margin-bottom: 8px; }
.history-item {
  width: 100%;
  text-align: left;
  border: 1px solid #eee;
  background: #fafafa;
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.history-item:hover { background: #f3f6fb; }
.history-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}
.history-current { color: #1976d2; font-weight: 600; }
.history-preview {
  font-size: 12px;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.history-meta { font-size: 11px; color: #888; }
.history-transcript {
  margin-top: 6px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  max-height: 280px;
  overflow-y: auto;
}
.history-open { display: inline-block; margin-bottom: 8px; }
.history-turn { margin-bottom: 8px; }
.history-turn-meta { font-size: 11px; color: #888; margin-bottom: 2px; }
.history-turn-text {
  font-size: 12px;
  color: #222;
  white-space: pre-wrap;
  word-break: break-word;
}
.history-turn.user .history-turn-text { color: #1f2937; }
.history-turn.ai .history-turn-meta,
.history-turn.agent .history-turn-meta { color: #1d4ed8; }
.order-row, .point-row {
  display: grid; grid-template-columns: 1fr auto auto; gap: 8px;
  width: 100%; text-align: left; border: 1px solid #eee; background: #fafafa;
  border-radius: 6px; padding: 8px; margin-bottom: 6px; font-size: 12px; cursor: pointer;
}
.point-row { cursor: default; }
.muted { color: #999; font-size: 13px; }
.link, .link-btn {
  color: #1976d2; background: none; border: none; cursor: pointer;
  font-size: 13px; text-decoration: none;
}
.btn {
  display: inline-flex; align-items: center; gap: 6px; border: none;
  border-radius: 4px; padding: 8px 12px; cursor: pointer; text-decoration: none; font-size: 13px;
}
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-sm { padding: 6px 10px; font-size: 12px; }
.btn-primary { background: #1976d2; color: #fff; }
.btn-secondary { background: #6b7280; color: #fff; }
.btn-danger-outline { background: transparent; color: #dc2626; border: 1px solid #dc2626; }
.modal-mask {
  position: fixed; inset: 0; background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.modal-card {
  background: #fff; border-radius: 10px; padding: 20px; width: min(420px, 92vw);
}
.modal-actions { display: flex; gap: 8px; margin-top: 16px; }

@media (max-width: 1024px) {
  .desk-layout { grid-template-columns: 1fr; }
  .queue-pane, .context-pane { max-height: 240px; }
}
</style>
